import React, { useState, useEffect } from 'react';
import { X, Save, Eye, EyeOff, ChevronDown, ChevronRight } from 'lucide-react';
import { DEFAULT_NEW_SESSION_FORM } from '../constants/defaultSessionForm';

function SessionModal({ isOpen, onClose, onSave, session = null, isEditing = false }) {
  const [formData, setFormData] = useState(() => ({ ...DEFAULT_NEW_SESSION_FORM }));

  const [showOAuthToken, setShowOAuthToken] = useState(false);
  const [showGroqKey, setShowGroqKey] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (isEditing && session) {
        setFormData({
          name: session.name || '',
          oauth_token: session.oauth_token || '',
          groq_api_key: session.groq_api_key || '',
          service_offerings: session.service_offerings || '',
          bid_writing_style: session.bid_writing_style || '',
          portfolio_links: session.portfolio_links || '',
          signature: session.signature || '',
          model_name: session.model_name || DEFAULT_NEW_SESSION_FORM.model_name,
          bid_limit: session.bid_limit != null ? session.bid_limit : DEFAULT_NEW_SESSION_FORM.bid_limit,
          project_search_limit:
            session.project_search_limit != null
              ? session.project_search_limit
              : DEFAULT_NEW_SESSION_FORM.project_search_limit,
          min_wait_time:
            session.min_wait_time != null ? session.min_wait_time : DEFAULT_NEW_SESSION_FORM.min_wait_time,
          skill_ids: session.skill_ids ? session.skill_ids.join(', ') : DEFAULT_NEW_SESSION_FORM.skill_ids,
          language_codes: session.language_codes ? session.language_codes.join(', ') : 'en',
          unwanted_currencies: session.unwanted_currencies
            ? session.unwanted_currencies.join(', ')
            : DEFAULT_NEW_SESSION_FORM.unwanted_currencies,
          unwanted_countries: session.unwanted_countries
            ? session.unwanted_countries.join(', ')
            : DEFAULT_NEW_SESSION_FORM.unwanted_countries,
        });
      } else {
        setFormData({ ...DEFAULT_NEW_SESSION_FORM });
      }
      setError(null);
    }
  }, [isOpen, isEditing, session]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Process form data
      const processedData = {
        ...formData,
        skill_ids: formData.skill_ids.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)),
        language_codes: formData.language_codes.split(',').map(s => s.trim()).filter(Boolean),
        unwanted_currencies: formData.unwanted_currencies.split(',').map(s => s.trim()).filter(Boolean),
        unwanted_countries: formData.unwanted_countries.split(',').map(s => s.trim()).filter(Boolean)
      };

      await onSave(processedData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save session');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-slate-900 border border-slate-700 rounded-lg text-left overflow-hidden shadow-2xl shadow-black/50 transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-slate-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-100">
                  {isEditing ? 'Edit Session' : 'Create New Session'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-slate-500 hover:text-slate-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-danger-950/40 border border-danger-800/50 rounded-lg">
                  <div className="text-danger-300">{error}</div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-slate-200">Basic Information</h4>
                  
                  <div>
                    <label className="label">Session Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="input"
                      placeholder="e.g., My Freelancer Account"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Freelancer OAuth Token</label>
                    <p className="text-xs text-slate-500 mb-1">
                      Paste your token when creating a session. It is not stored in frontend source code.
                    </p>
                    <div className="relative">
                      <input
                        type={showOAuthToken ? "text" : "password"}
                        value={formData.oauth_token}
                        onChange={(e) => handleInputChange('oauth_token', e.target.value)}
                        className="input pr-10"
                        placeholder="Enter your OAuth token"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowOAuthToken(!showOAuthToken)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-200"
                      >
                        {showOAuthToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="label">Groq API Key</label>
                    <p className="text-xs text-slate-500 mb-1">
                      From console.groq.com. Use Diagnostics after save to verify the key.
                    </p>
                    <div className="relative">
                      <input
                        type={showGroqKey ? "text" : "password"}
                        value={formData.groq_api_key}
                        onChange={(e) => handleInputChange('groq_api_key', e.target.value)}
                        className="input pr-10"
                        placeholder="Enter your Groq API key"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowGroqKey(!showGroqKey)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-200"
                      >
                        {showGroqKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="label">Signature</label>
                    <input
                      type="text"
                      value={formData.signature}
                      onChange={(e) => handleInputChange('signature', e.target.value)}
                      className="input"
                      placeholder="Your name for bids"
                    />
                  </div>
                </div>

                {/* Bot Settings */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-slate-200">Bot Settings</h4>
                  
                  <div>
                    <label className="label">Bid Limit</label>
                    <input
                      type="number"
                      value={formData.bid_limit}
                      onChange={(e) => handleInputChange('bid_limit', parseInt(e.target.value) || 0)}
                      className="input"
                      min="1"
                      max="1000"
                    />
                  </div>

                  <div>
                    <label className="label">Project Search Limit</label>
                    <input
                      type="number"
                      value={formData.project_search_limit}
                      onChange={(e) => handleInputChange('project_search_limit', parseInt(e.target.value) || 0)}
                      className="input"
                      min="1"
                      max="100"
                    />
                  </div>

                  <div>
                    <label className="label">Minimum Wait Time (seconds)</label>
                    <input
                      type="number"
                      value={formData.min_wait_time}
                      onChange={(e) => handleInputChange('min_wait_time', parseInt(e.target.value) || 0)}
                      className="input"
                      min="1"
                      max="300"
                    />
                  </div>

                  <div>
                    <label className="label">Groq model name</label>
                    <input
                      type="text"
                      value={formData.model_name}
                      onChange={(e) => handleInputChange('model_name', e.target.value)}
                      className="input font-mono text-sm"
                      placeholder="qwen/qwen3-32b"
                    />
                    <p className="text-xs text-slate-500 mt-1">Llama-style model ids are blocked server-side; use Qwen or another allowed model.</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 border border-slate-700 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-slate-800/80 text-left text-sm font-medium text-slate-200 hover:bg-slate-800"
                >
                  {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Advanced — skills, languages, geo filters
                </button>
                {showAdvanced && (
                  <div className="p-4 space-y-4 border-t border-slate-700 bg-slate-900/80">
                    <div>
                      <label className="label">Skill IDs (comma-separated)</label>
                      <textarea
                        value={formData.skill_ids}
                        onChange={(e) => handleInputChange('skill_ids', e.target.value)}
                        className="input font-mono text-xs"
                        rows={4}
                        placeholder="3, 9, 13, ..."
                      />
                    </div>
                    <div>
                      <label className="label">Language codes</label>
                      <input
                        type="text"
                        value={formData.language_codes}
                        onChange={(e) => handleInputChange('language_codes', e.target.value)}
                        className="input font-mono text-sm"
                        placeholder="en"
                      />
                    </div>
                    <div>
                      <label className="label">Unwanted currencies (comma-separated)</label>
                      <input
                        type="text"
                        value={formData.unwanted_currencies}
                        onChange={(e) => handleInputChange('unwanted_currencies', e.target.value)}
                        className="input font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="label">Unwanted countries (comma-separated, lowercase)</label>
                      <textarea
                        value={formData.unwanted_countries}
                        onChange={(e) => handleInputChange('unwanted_countries', e.target.value)}
                        className="input font-mono text-xs"
                        rows={3}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Service Offerings */}
              <div className="mt-6">
                <h4 className="text-md font-medium text-slate-200 mb-4">Service Offerings</h4>
                <textarea
                  value={formData.service_offerings}
                  onChange={(e) => handleInputChange('service_offerings', e.target.value)}
                  className="input font-mono text-sm"
                  rows="14"
                  placeholder="Describe your services that the bot should look for..."
                />
              </div>

              {/* Bid Writing Style */}
              <div className="mt-6">
                <h4 className="text-md font-medium text-slate-200 mb-4">Bid Writing Style</h4>
                <textarea
                  value={formData.bid_writing_style}
                  onChange={(e) => handleInputChange('bid_writing_style', e.target.value)}
                  className="input font-mono text-sm"
                  rows="18"
                  placeholder="Customize how the AI writes your bids..."
                />
              </div>

              {/* Portfolio Links */}
              <div className="mt-6">
                <h4 className="text-md font-medium text-slate-200 mb-4">Portfolio Links</h4>
                <textarea
                  value={formData.portfolio_links}
                  onChange={(e) => handleInputChange('portfolio_links', e.target.value)}
                  className="input font-mono text-sm"
                  rows="20"
                  placeholder="Enter your portfolio links (one per line)..."
                />
              </div>
            </div>

            <div className="bg-slate-950/80 border-t border-slate-800 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{isLoading ? 'Saving...' : (isEditing ? 'Update Session' : 'Create Session')}</span>
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary mt-3 sm:mt-0 sm:mr-3"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SessionModal;




